import type { ElementNode } from '@payloadcms/richtext-slate';
import type { UploadedImage } from '@prezly/uploadcare';
import classNames from 'classnames';

import { stringifySlateContent } from '@/lib/stringifySlateContent';
import { PayloadSlateRenderer } from '@/src/components/PayloadSlateRenderer/PayloadSlateRenderer';
import type { DataAttributes, Media } from '@/types';
import { slugify } from '@/util/strings/slugify';

import { Avatar } from './components/Avatar';
import { Customer } from './components/Customer';
import { ExtrasRow } from './components/ExtrasRow';
import { Link } from './components/Link';
import { ReviewScore } from './components/ReviewScore';

import styles from './InlineQuote.module.scss';

interface Props extends DataAttributes {
    author?: string;
    body: ElementNode[];
    className?: string;
    company?: string;
    companyUrl?: string;
    title?: string;
    reviewScore?: string;
    reviewUrl?: string;
    storyPath?: string;
    avatar?: Media;
}

export function InlineQuote({
    author,
    body,
    company,
    className,
    storyPath,
    companyUrl,
    reviewUrl,
    reviewScore,
    avatar,
    ...attributes
}: Props) {
    const WrapperTag = typeof body === 'string' ? 'p' : 'div';
    const linkHref = storyPath || reviewUrl;

    return (
        <div
            data-inline-quote={slugify(stringifySlateContent(body))}
            data-inline-quote-author={author}
            data-inline-quote-company={company}
            data-inline-quote-review-score={reviewScore}
            {...attributes}
            className={classNames(styles.container, className)}
        >
            <WrapperTag className={styles.quote} data-quote-body>
                <PayloadSlateRenderer nodes={body} />
            </WrapperTag>
            {author && company && (
                <div className={styles.customer} data-inline-quote-customer>
                    {avatar && (
                        <Avatar
                            author={author}
                            src={avatar.uploadcare as UploadedImage | undefined}
                            size="tiny"
                        />
                    )}
                    <Customer author={author} company={company} companyUrl={companyUrl} />
                </div>
            )}
            {(reviewScore || storyPath) && (
                <ExtrasRow data-inline-quote-extra-row>
                    {reviewScore && <ReviewScore reviewScore={reviewScore} />}

                    {linkHref && <Link href={linkHref} />}
                </ExtrasRow>
            )}
        </div>
    );
}
